import { Card } from '../../../components/card';
import { PageHead } from '../../../components/page-head';
// student-cheats-page.tsx — Dev/testing tools: award XP, badges, endorsements,
// skill points, and seed portfolio data (campaign role + accomplishments).
// Only visible to authenticated students; endpoints require JWT + STUDENT role.
import { useState } from 'react';
import { Button } from '../../../components/button';
import { useTranslation } from 'react-i18next';
import { useStudentDevActions } from '../../../api/student/dev/hooks';
import { getAchievementImage } from '../../../assets/achievements/achievement-images';
import { ACHIEVEMENT_REGISTRY } from '../../../constants/achievement-registry';
import { useSkills } from '../../../api/student/skills/hooks';
import { useRoles } from '../../../api/student/roles/hooks';
import { useEndorsementTags } from '../../../api/student/endorsement-tags/hooks';
import { useStudentCampaigns } from '../../../api/student/campaigns/hooks';
import type { StudentProgress } from '../../../api/student/progress/types';
import { useStudentPortfolio } from '../../../api/student/portfolio/hooks';
import { endorsementTag } from '../endorsement-tag';
// Dev-only tooling page — keeps the legacy design-system tokens/primitives.
import styles from './student-cheats-page.module.css';

const XP_AMOUNTS = [50, 100, 200];
// Per-tag endorsement adjuster — same compact pattern as the per-skill row below.
const ENDORSEMENT_DELTAS = [-1, 1, 5];
const SKILL_POINT_DELTAS = [-25, -10, 10, 25];

interface StudentCheatsPageProps {
  progress: StudentProgress | null;
}

export function StudentCheatsPage({ progress }: StudentCheatsPageProps) {
  const { t } = useTranslation();
  const [loadingXp, setLoadingXp] = useState<number | null>(null);
  const [loadingBadge, setLoadingBadge] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const { data: skills = [] } = useSkills();
  const { data: roles = [] } = useRoles();
  const { data: endorsementTags = [] } = useEndorsementTags();
  const { data: campaigns = [] } = useStudentCampaigns();
  const actions = useStudentDevActions();
  const [campaignId, setCampaignId] = useState('');
  const [certCampaignId, setCertCampaignId] = useState('');
  const [accomplishmentText, setAccomplishmentText] = useState('');
  const [roleKey, setRoleKey] = useState('');
  const [teamSize, setTeamSize] = useState('');

  // Read existing accomplishments straight from the portfolio cache so
  // adding / removing one updates the list as soon as the portfolio query
  // is invalidated by the mutation.
  const portfolioQuery = useStudentPortfolio();
  const selectedCampaign = portfolioQuery.data?.campaigns.find((c) => c.id === campaignId);
  const accomplishmentsForCampaign = selectedCampaign?.accomplishments ?? [];

  function showFeedback(type: 'success' | 'error', msg: string) {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 3000);
  }

  async function handleAwardXp(amount: number) {
    setLoadingXp(amount);
    try {
      const data = await actions.awardXp(amount);
      showFeedback('success', t('student.cheats.xpGranted', { amount, total: data.xpPoints }));
    } catch {
      showFeedback('error', t('student.cheats.error'));
    } finally {
      setLoadingXp(null);
    }
  }

  async function handleAwardBadge(achievementId: string) {
    setLoadingBadge(achievementId);
    try {
      const data = await actions.awardBadge(achievementId);
      if (data.created) {
        showFeedback('success', t('student.cheats.badgeGranted'));
        } else {
        showFeedback('error', t('student.cheats.badgeAlreadyOwned'));
      }
    } catch {
      showFeedback('error', t('student.cheats.error'));
    } finally {
      setLoadingBadge(null);
    }
  }

  async function handleRemoveBadge(achievementId: string) {
    setLoadingBadge(achievementId);
    try {
      await actions.removeBadge(achievementId);
      showFeedback('success', t('student.cheats.badgeRemoved'));
    } catch {
      showFeedback('error', t('student.cheats.error'));
    } finally {
      setLoadingBadge(null);
    }
  }

  async function handleAdjustEndorsement(tagId: string, delta: number) {
    setBusy(true);
    try {
      const data = await actions.adjustEndorsement(tagId, delta);
      const tagName = endorsementTag(t, tagId).name;
      showFeedback('success', t('student.cheats.endorsementUpdated', { tag: tagName, total: data.count }));
    } catch {
      showFeedback('error', t('student.cheats.error'));
    } finally {
      setBusy(false);
    }
  }

  async function handleAdjustSkill(skillId: string, delta: number) {
    setBusy(true);
    try {
      await actions.adjustSkillPoints(skillId, delta);
      const signed = delta > 0 ? `+${delta}` : `${delta}`;
      showFeedback('success', t('student.cheats.skillPointsAdjusted', { delta: signed }));
    } catch {
      showFeedback('error', t('student.cheats.error'));
    } finally {
      setBusy(false);
    }
  }

  async function handleAddAccomplishment() {
    if (!campaignId || !accomplishmentText.trim()) return;
    setBusy(true);
    try {
      await actions.addAccomplishment(campaignId, accomplishmentText.trim());
      showFeedback('success', t('student.cheats.accomplishmentAdded'));
      setAccomplishmentText('');
    } catch {
      showFeedback('error', t('student.cheats.error'));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteAccomplishment(id: string) {
    setBusy(true);
    try {
      await actions.deleteAccomplishment(id);
      showFeedback('success', t('student.cheats.accomplishmentRemoved'));
    } catch {
      showFeedback('error', t('student.cheats.error'));
    } finally {
      setBusy(false);
    }
  }

  async function handleUnlockCertificate() {
    if (!certCampaignId) return;
    setBusy(true);
    try {
      await actions.unlockCertificate(certCampaignId);
      showFeedback('success', t('student.cheats.certificateUnlocked'));
    } catch {
      showFeedback('error', t('student.cheats.error'));
    } finally {
      setBusy(false);
    }
  }

  async function handleSetRole() {
    if (!campaignId) return;
    setBusy(true);
    try {
      // Team name is no longer user-editable here — the backend derives it
      // from the cohort the student shares with this campaign.
      await actions.setCampaignRole(campaignId, roleKey.trim() || undefined, teamSize ? Number(teamSize) : undefined);
      showFeedback('success', t('student.cheats.roleUpdated'));
    } catch {
      showFeedback('error', t('student.cheats.error'));
    } finally {
      setBusy(false);
    }
  }

  const ownedBadges = new Set((progress?.badges ?? []).map((b) => b.achievementId));

  return (
    <div className="sp-page">
      <div className="sp-page__header">
        <div>
          <h1 className="sp-page__title">{t('student.cheats.title')}</h1>
          <p className="sp-page__subtitle">{t('student.cheats.subtitle')}</p>
        </div>
      </div>

      {feedback && (
        <div
          className={`${styles.feedback} ${
            feedback.type === 'success' ? styles.feedbackSuccess : styles.feedbackError
          }`}
        >
          {feedback.msg}
        </div>
      )}

      <Card as="section" framed className={styles.section}>
        <PageHead level="section" tight>{t('student.cheats.xpTitle')}</PageHead>
        {progress && (
          <p className={styles.current}>
            {t('student.cheats.currentXp', { xp: progress.xpPoints, level: progress.level })}
          </p>
        )}
        <div className={styles.xpButtons}>
          {XP_AMOUNTS.map((amount) => (
            <Button key={amount} onClick={() => handleAwardXp(amount)} disabled={loadingXp !== null}>
              {loadingXp === amount ? '...' : `+${amount} XP`}
            </Button>
          ))}
        </div>
      </Card>

      <Card as="section" framed className={styles.section}>
        <PageHead level="section" tight>{t('student.cheats.endorsementsTitle')}</PageHead>
        {endorsementTags.length === 0 ? (
          <p className={styles.current}>{t('student.cheats.noEndorsementTags')}</p>
        ) : (
          <div className={styles.skillList}>
            {endorsementTags.map((tag) => (
              <div key={tag.id} className={styles.skillRow}>
                <span className={styles.skillName}>
                  {endorsementTag(t, tag.id).name}
                </span>
                <div className={styles.skillButtons}>
                  {ENDORSEMENT_DELTAS.map((delta) => (
                    <Button variant="gold" key={delta} onClick={() => handleAdjustEndorsement(tag.id, delta)} disabled={busy}>
                      {delta > 0 ? `+${delta}` : delta}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card as="section" framed className={styles.section}>
        <PageHead level="section" tight>{t('student.cheats.skillPointsTitle')}</PageHead>
        {skills.length === 0 ? (
          <p className={styles.current}>{t('student.cheats.noSkills')}</p>
        ) : (
          <div className={styles.skillList}>
            {skills.map((skill) => (
              <div key={skill.id} className={styles.skillRow}>
                <span className={styles.skillName}>
                  {t(`skills.${skill.id}.name`, { defaultValue: skill.id })}
                </span>
                <div className={styles.skillButtons}>
                  {SKILL_POINT_DELTAS.map((delta) => (
                    <Button variant="gold" key={delta} onClick={() => handleAdjustSkill(skill.id, delta)} disabled={busy}>
                      {delta > 0 ? `+${delta}` : delta}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card as="section" framed className={styles.section}>
        <PageHead level="section" tight>{t('student.cheats.portfolioTitle')}</PageHead>
        <label className={styles.field}>
          <span>{t('student.cheats.campaign')}</span>
          <select
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className={styles.input}
          >
            <option value="">{t('student.cheats.selectCampaign')}</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>

        <div className={styles.field}>
          <span>{t('student.cheats.roleAndTeam')}</span>
          <div className={styles.inline}>
            <select
                value={roleKey}
                onChange={(e) => setRoleKey(e.target.value)}
                aria-label={t('student.cheats.selectRole')}
                className={styles.input}
            >
              <option value="">{t('student.cheats.selectRole')}</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {t(`roles.${r.id}.name`, { defaultValue: r.id })}
                </option>
              ))}
            </select>
            <input
              type="number"
              value={teamSize}
              onChange={(e) => setTeamSize(e.target.value)}
              placeholder={t('student.cheats.teamSizePlaceholder')}
              className={`${styles.input} ${styles.inputNarrow}`}
            />
            <Button onClick={handleSetRole} disabled={busy || !campaignId}>
              {t('student.cheats.setRole')}
            </Button>
          </div>
        </div>

        <div className={styles.field}>
          <span>{t('student.cheats.accomplishment')}</span>
          <div className={styles.inline}>
            <input
              type="text"
              value={accomplishmentText}
              onChange={(e) => setAccomplishmentText(e.target.value)}
              placeholder={t('student.cheats.accomplishmentPlaceholder')}
              className={styles.input}
            />
            <Button onClick={handleAddAccomplishment} disabled={busy || !campaignId || !accomplishmentText.trim()}>
              {t('student.cheats.addAccomplishment')}
            </Button>
          </div>

          {/* Existing accomplishments for the selected campaign — each row
              has a discrete × button to remove it. */}
          {campaignId && accomplishmentsForCampaign.length > 0 && (
            <ul className={styles.accList}>
              {accomplishmentsForCampaign.map((a) => (
                <li key={a.id} className={styles.accRow}>
                  <span className={styles.accText}>{a.text}</span>
                  <button
                    type="button"
                    className={styles.accRemove}
                    onClick={() => handleDeleteAccomplishment(a.id)}
                    disabled={busy}
                    aria-label={t('student.cheats.removeAccomplishment')}
                    title={t('student.cheats.removeAccomplishment')}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      <Card as="section" framed className={styles.section}>
        <PageHead level="section" tight>{t('student.cheats.certificateTitle')}</PageHead>
        <p className={styles.current}>{t('student.cheats.certificateHint')}</p>
        <div className={styles.inline}>
          <select
            value={certCampaignId}
            onChange={(e) => setCertCampaignId(e.target.value)}
            aria-label={t('student.cheats.campaign')}
            className={styles.input}
          >
            <option value="">{t('student.cheats.selectCampaign')}</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <Button onClick={handleUnlockCertificate} disabled={busy || !certCampaignId}>
            {t('student.cheats.unlockCertificate')}
          </Button>
        </div>
      </Card>

      <section>
        <PageHead level="section" tight>{t('student.cheats.badgesTitle')}</PageHead>
        <div className={styles.badgesGrid}>
          {Object.entries(ACHIEVEMENT_REGISTRY).map(([achievementId, def]) => {
            const image = getAchievementImage(def.imageId);
            const owned = ownedBadges.has(achievementId);
            return (
              <Card
                key={achievementId}
                className={`${styles.badgeCard} ${owned ? styles.badgeCardOwned : ''}`}
              >
                {image && (
                  <img
                    src={image}
                    alt={achievementId}
                    className={styles.badgeImg}
                  />
                )}
                <span className={styles.badgeName}>
                  {t(def.nameKey, { defaultValue: achievementId })}
                </span>
                {owned ? (
                  <Button
                    type="button"
                    variant="dangerQuiet"
                    size="sm"
                    onClick={() => handleRemoveBadge(achievementId)}
                    disabled={loadingBadge !== null}
                  >
                    {loadingBadge === achievementId ? '...' : t('student.cheats.removeBadge')}
                  </Button>
                ) : (
                  <Button onClick={() => handleAwardBadge(achievementId)} disabled={loadingBadge !== null}>
                    {loadingBadge === achievementId ? '...' : t('student.cheats.grantBadge')}
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}
